<template>
    <div class="android-file-selector">
        <div class="file-selector-container">
            <!-- Кнопки для выбора файлов -->
            <div class="file-buttons">
                <b-button 
                    v-for="(input, index) in fileInputs" 
                    :key="index"
                    @click="triggerFileInput(index)"
                    variant="primary" 
                    size="sm"
                    class="file-button"
                    :disabled="isInputUsed(index)"
                >
                    <i class="fas fa-camera mr-2"></i>
                    {{ getButtonText(index) }}
                </b-button>
            </div>
            
            <!-- Кнопка добавления еще файлов -->
            <b-button 
                v-if="canAddMore"
                @click="addFileInput"
                variant="secondary" 
                size="sm"
                class="add-more-button"
            >
                <i class="fas fa-plus mr-2"></i>
                Добавить еще файл
            </b-button>
            
            <!-- Кнопка очистки -->
            <b-button 
                v-if="selectedFiles.length > 0"
                @click="clearAll"
                variant="danger" 
                size="sm"
                class="clear-button"
            >
                <i class="fas fa-trash mr-2"></i>
                Очистить все
            </b-button>
        </div>
        
        <!-- Список выбранных файлов -->
        <div v-if="selectedFiles.length > 0" class="selected-files">
            <div 
                v-for="(file, index) in selectedFiles" 
                :key="index"
                class="file-item"
            >
                <div class="file-info">
                    <i class="fas fa-file-image mr-2"></i>
                    <span class="file-name">{{ file.name }}</span>
                    <span class="file-size">({{ formatFileSize(file.size) }})</span>
                </div>
                <b-button 
                    @click="removeFile(index)"
                    variant="outline-danger" 
                    size="sm"
                    class="remove-button"
                >
                    <i class="fas fa-times"></i>
                </b-button>
            </div>
        </div>
        
        <!-- Скрытые input элементы -->
        <div style="display: none;">
            <input 
                v-for="(input, index) in fileInputs" 
                :key="index"
                :ref="`fileInput${index}`"
                type="file" 
                :accept="accept"
                @change="onFileSelected(index, $event)"
            />
        </div>
    </div>
</template>

<script>
export default {
    name: 'AndroidFileSelector',
    props: {
        multiple: {
            type: Boolean,
            default: true
        },
        accept: {
            type: String,
            default: 'image/*, video/*'
        },
        maxCount: {
            type: Number,
            default: 10
        },
        value: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            fileInputs: [],
            selectedFiles: [],
            usedInputs: new Set()
        };
    },
    computed: {
        canAddMore() {
            return this.multiple && this.selectedFiles.length < this.maxCount;
        }
    },
    watch: {
        value: {
            handler(newValue) {
                this.selectedFiles = [...newValue];
            },
            immediate: true
        },
        selectedFiles: {
            handler(newValue) {
                this.$emit('input', newValue);
                this.$emit('change', newValue);
            },
            deep: true
        }
    },
    mounted() {
        // Создаем первый input
        this.addFileInput();
    },
    methods: {
        addFileInput() {
            if (this.fileInputs.length < this.maxCount) {
                this.fileInputs.push({
                    id: Date.now() + Math.random(),
                    used: false
                });
            }
        },
        
        triggerFileInput(index) {
            const inputRef = this.$refs[`fileInput${index}`];
            if (inputRef && inputRef[0]) {
                inputRef[0].click();
            }
        },
        
        isInputUsed(index) {
            return this.usedInputs.has(index);
        },
        
        getButtonText(index) {
            if (this.isInputUsed(index)) {
                return `Файл ${index + 1} (использован)`;
            }
            return `Выбрать файл ${index + 1}`;
        },
        
        onFileSelected(index, event) {
            const file = event.target.files[0];
            if (file) {
                // Проверяем тип файла
                if (!this.isValidFileType(file)) {
                    this.$bvToast.toast('Неподдерживаемый тип файла', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    });
                    return;
                }
                
                // Проверяем размер файла (максимум 50MB)
                if (file.size > 50 * 1024 * 1024) {
                    this.$bvToast.toast('Файл слишком большой (максимум 50MB)', {
                        title: 'Ошибка',
                        variant: 'danger',
                        solid: true
                    });
                    return;
                }
                
                // Добавляем файл
                this.selectedFiles.push(file);
                this.usedInputs.add(index);
                
                // Создаем новый input если нужно
                if (this.canAddMore && this.fileInputs.length < this.maxCount) {
                    this.addFileInput();
                }
                
                // Очищаем input
                event.target.value = '';
            }
        },
        
        isValidFileType(file) {
            const validTypes = [
                'image/jpeg',
                'image/jpg', 
                'image/jfif',
                'image/png',
                'image/gif',
                'image/webp',
                'video/mp4',
                'video/avi',
                'video/mov',
                'video/wmv'
            ];
            
            return validTypes.includes(file.type) || 
                   file.name.match(/\.(jpg|jpeg|jfif|png|gif|webp|mp4|avi|mov|wmv)$/i);
        },
        
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },
        
        removeFile(index) {
            this.selectedFiles.splice(index, 1);
            
            // Освобождаем input
            const usedIndex = Array.from(this.usedInputs)[index];
            if (usedIndex !== undefined) {
                this.usedInputs.delete(usedIndex);
            }
        },
        
        clearAll() {
            this.selectedFiles = [];
            this.usedInputs.clear();
            
            // Очищаем все input'ы
            this.fileInputs.forEach((input, index) => {
                const inputRef = this.$refs[`fileInput${index}`];
                if (inputRef && inputRef[0]) {
                    inputRef[0].value = '';
                }
            });
        }
    }
};
</script>

<style scoped>
.android-file-selector {
    width: 100%;
}

.file-selector-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 15px;
    border: 2px dashed #ccc;
    border-radius: 8px;
    background: #f9f9f9;
}

.file-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.file-button {
    min-width: 140px;
}

.add-more-button {
    align-self: flex-start;
}

.clear-button {
    align-self: flex-start;
}

.selected-files {
    margin-top: 15px;
}

.file-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: #e9ecef;
    border-radius: 4px;
    margin-bottom: 8px;
}

.file-info {
    display: flex;
    align-items: center;
    flex: 1;
    margin-right: 10px;
}

.file-name {
    font-weight: 500;
    margin-right: 8px;
}

.file-size {
    color: #6c757d;
    font-size: 0.875em;
}

.remove-button {
    padding: 2px 8px;
    font-size: 12px;
}

/* Стили для отключенных кнопок */
.file-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
